/**
 * Lazy Loading Fallback Components
 *
 * Provides various loading states for lazy-loaded components
 * with proper accessibility and visual feedback.
 */
import React from 'react';
import { useThemeStore } from '@/stores/themeStore';

/**
 * Full page loader for route-level lazy loading
 */
export const PageLoader: React.FC<{ message?: string }> = ({ message = 'Loading...' }) => {
  const { resolvedTheme } = useThemeStore();
  const isDark = resolvedTheme === 'dark';

  return (
    <div
      className={`flex min-h-screen items-center justify-center px-4 ${
        isDark ? 'mm-workplace mm-workplace-bg text-white' : 'mm-light mm-light-bg text-gray-900'
      }`}
      role="status"
      aria-live="polite"
      aria-busy="true"
    >
      <div className="mm-glass rounded-3xl p-8 text-center shadow-2xl">
        <div
          className="mb-4 h-12 w-12 animate-spin rounded-full border-4 border-white/20 border-t-violet-400 mx-auto"
          aria-hidden="true"
        />
        <p className={isDark ? 'text-white/70' : 'text-gray-600'}>{message}</p>
        <span className="sr-only">Loading page content, please wait</span>
      </div>
    </div>
  );
};

/**
 * Skeleton loader for content areas
 */
export const ContentSkeleton: React.FC<{ lines?: number }> = ({ lines = 3 }) => {
  const { resolvedTheme } = useThemeStore();
  const isDark = resolvedTheme === 'dark';

  return (
    <div
      className={`animate-pulse space-y-4 p-6 ${isDark ? 'bg-gray-800/50' : 'bg-gray-100'} rounded-xl`}
      role="status"
      aria-label="Loading content"
    >
      {/* Header skeleton */}
      <div className={`h-8 ${isDark ? 'bg-gray-700' : 'bg-gray-300'} rounded w-1/3`} />

      {/* Content lines skeleton */}
      {Array.from({ length: lines }).map((_, i) => (
        <div key={i} className="space-y-2">
          <div
            className={`h-4 ${isDark ? 'bg-gray-700' : 'bg-gray-300'} rounded`}
            style={{ width: `${100 - i * 10}%` }}
          />
        </div>
      ))}
      <span className="sr-only">Loading content, please wait</span>
    </div>
  );
};

/**
 * Card skeleton for dashboard widgets
 */
export const CardSkeleton: React.FC = () => {
  const { resolvedTheme } = useThemeStore();
  const isDark = resolvedTheme === 'dark';

  return (
    <div
      className={`animate-pulse p-6 ${isDark ? 'mm-glass' : 'bg-white shadow-md'} rounded-2xl`}
      role="status"
      aria-label="Loading card"
    >
      <div className="flex justify-between items-start mb-4">
        <div className={`h-4 ${isDark ? 'bg-gray-700' : 'bg-gray-300'} rounded w-24`} />
        <div className={`h-8 w-8 ${isDark ? 'bg-gray-700' : 'bg-gray-300'} rounded-lg`} />
      </div>
      <div className={`h-8 ${isDark ? 'bg-gray-700' : 'bg-gray-300'} rounded w-16 mb-2`} />
      <div className={`h-3 ${isDark ? 'bg-gray-700' : 'bg-gray-300'} rounded w-32`} />
      <span className="sr-only">Loading card content</span>
    </div>
  );
};

/**
 * Table skeleton for data tables
 */
export const TableSkeleton: React.FC<{ rows?: number; columns?: number }> = ({
  rows = 5,
  columns = 4,
}) => {
  const { resolvedTheme } = useThemeStore();
  const isDark = resolvedTheme === 'dark';
  const bgClass = isDark ? 'bg-gray-700' : 'bg-gray-300';
  const rowBgClass = isDark ? 'bg-gray-800' : 'bg-gray-50';

  return (
    <div
      className={`animate-pulse ${isDark ? 'mm-glass' : 'bg-white shadow-md'} rounded-xl overflow-hidden`}
      role="status"
      aria-label="Loading table"
    >
      {/* Header */}
      <div className={`flex gap-4 p-4 ${rowBgClass}`}>
        {Array.from({ length: columns }).map((_, i) => (
          <div key={i} className={`h-4 ${bgClass} rounded flex-1`} />
        ))}
      </div>

      {/* Rows */}
      {Array.from({ length: rows }).map((_, rowIdx) => (
        <div
          key={rowIdx}
          className={`flex gap-4 p-4 border-t ${isDark ? 'border-gray-700' : 'border-gray-200'}`}
        >
          {Array.from({ length: columns }).map((_, colIdx) => (
            <div
              key={colIdx}
              className={`h-4 ${bgClass} rounded flex-1`}
              style={{ opacity: 0.7 - rowIdx * 0.1 }}
            />
          ))}
        </div>
      ))}
      <span className="sr-only">Loading table data</span>
    </div>
  );
};

/**
 * Form skeleton for settings/form pages
 */
export const FormSkeleton: React.FC<{ fields?: number }> = ({ fields = 4 }) => {
  const { resolvedTheme } = useThemeStore();
  const isDark = resolvedTheme === 'dark';
  const bgClass = isDark ? 'bg-gray-700' : 'bg-gray-300';

  return (
    <div
      className={`animate-pulse space-y-6 p-6 ${isDark ? 'mm-glass' : 'bg-white shadow-md'} rounded-xl`}
      role="status"
      aria-label="Loading form"
    >
      {Array.from({ length: fields }).map((_, i) => (
        <div key={i} className="space-y-2">
          <div className={`h-4 ${bgClass} rounded w-24`} />
          <div className={`h-10 ${bgClass} rounded w-full`} />
        </div>
      ))}
      <div className="flex gap-4 pt-4">
        <div className={`h-10 ${bgClass} rounded w-24`} />
        <div className={`h-10 ${bgClass} rounded w-24`} />
      </div>
      <span className="sr-only">Loading form</span>
    </div>
  );
};

/**
 * Analytics/Chart skeleton
 */
export const ChartSkeleton: React.FC = () => {
  const { resolvedTheme } = useThemeStore();
  const isDark = resolvedTheme === 'dark';
  const bgClass = isDark ? 'bg-gray-700' : 'bg-gray-300';

  return (
    <div
      className={`animate-pulse p-6 ${isDark ? 'mm-glass' : 'bg-white shadow-md'} rounded-2xl`}
      role="status"
      aria-label="Loading chart"
    >
      <div className={`h-6 ${bgClass} rounded w-48 mb-6`} />
      <div className="flex items-end gap-2 h-48">
        {[40, 65, 45, 80, 55, 70, 60, 75, 50, 85, 65, 70].map((height, i) => (
          <div
            key={i}
            className={`${bgClass} rounded-t flex-1`}
            style={{ height: `${height}%` }}
          />
        ))}
      </div>
      <span className="sr-only">Loading chart data</span>
    </div>
  );
};

/**
 * Error fallback for failed lazy loads
 */
export const LoadError: React.FC<{
  error?: Error;
  onRetry?: () => void;
}> = ({ error, onRetry }) => {
  const { resolvedTheme } = useThemeStore();
  const isDark = resolvedTheme === 'dark';

  return (
    <div
      className={`flex min-h-screen items-center justify-center px-4 ${
        isDark ? 'mm-workplace mm-workplace-bg text-white' : 'mm-light mm-light-bg text-gray-900'
      }`}
      role="alert"
    >
      <div className="mm-glass rounded-3xl p-8 text-center shadow-2xl max-w-md">
        <div className="mb-4 text-red-400 text-5xl">!</div>
        <h2 className={`text-xl font-semibold mb-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>
          Failed to Load
        </h2>
        <p className={`mb-6 ${isDark ? 'text-white/70' : 'text-gray-600'}`}>
          {error?.message || 'Something went wrong while loading this page.'}
        </p>
        {onRetry && (
          <button
            onClick={onRetry}
            className="px-6 py-2 bg-violet-500 hover:bg-violet-600 text-white rounded-xl transition-colors focus:outline-none focus:ring-2 focus:ring-violet-400 focus:ring-offset-2"
          >
            Try Again
          </button>
        )}
      </div>
    </div>
  );
};

/**
 * Inline spinner for smaller components
 */
export const InlineSpinner: React.FC<{ size?: 'sm' | 'md' | 'lg' }> = ({ size = 'md' }) => {
  const sizeClasses = {
    sm: 'h-4 w-4 border-2',
    md: 'h-6 w-6 border-2',
    lg: 'h-8 w-8 border-3',
  };

  return (
    <div
      className={`animate-spin rounded-full border-white/20 border-t-violet-400 ${sizeClasses[size]}`}
      role="status"
      aria-label="Loading"
    >
      <span className="sr-only">Loading...</span>
    </div>
  );
};

export default {
  PageLoader,
  ContentSkeleton,
  CardSkeleton,
  TableSkeleton,
  FormSkeleton,
  ChartSkeleton,
  LoadError,
  InlineSpinner,
};
